import * as Sentry from "@sentry/tanstackstart-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import React, { Component, type ReactNode, Suspense } from "react";

import { CityDirectory } from "~/components/city-directory";
import { Heading } from "~/components/ui/heading";
import type { RegionWithCountriesOutputGroup } from "~/lib/server/cities";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
} from "~/lib/shared/meta";

const LazyCoachMap = React.lazy(() =>
  import("~/components/coach-map").then((m) => ({ default: m.CoachMap })),
);

export const getCities = createServerFn({ method: "GET" }).handler(async () => {
  const { getPopulatedCities } = await import("~/lib/server/cities");
  const rows = await getPopulatedCities();
  return rows.map((r) => ({
    value: r.slug,
    label: r.label,
    coordinates: [r.longitude ?? 0, r.latitude ?? 0] as [number, number],
  }));
});

export const getCityOptions = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getPopulatedCitiesGrouped } = await import("~/lib/server/cities");
    return await getPopulatedCitiesGrouped();
  },
);

function MapLoadingSkeleton() {
  return (
    <div className="flex h-full items-center justify-center bg-gray-900">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-purple-800 border-t-purple-400" />
        <p className="text-sm text-gray-500">Loading map...</p>
      </div>
    </div>
  );
}

function MapUnavailableFallback() {
  return (
    <div className="flex h-full items-center justify-center bg-gray-900 px-6 text-center">
      <p className="max-w-sm text-sm text-gray-500">
        The interactive map isn't available on this device. Browse coaches by
        city in the directory below.
      </p>
    </div>
  );
}

/**
 * Catches synchronous render throws from the lazy-loaded map (e.g. maplibre's
 * "Could not create a WebGL context" on clients without working WebGL). React
 * `<Suspense>` does not catch render errors, so without this boundary a WebGL
 * init failure crashes the whole city page. The map is an enhancement — the
 * page's directory still works — so we degrade to a quiet placeholder.
 */
class MapErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error, { tags: { surface: "coaches_city_map" } });
  }

  render() {
    if (this.state.hasError) {
      return <MapUnavailableFallback />;
    }
    return this.props.children;
  }
}

function CoachesCityIndexPage() {
  const { data: cities = [] } = useQuery({
    queryKey: ["cities"],
    queryFn: () => getCities(),
  });
  const { data: cityOptions = [] } = useQuery({
    queryKey: ["cityOptions"],
    queryFn: () => getCityOptions(),
  });

  return (
    <div className="relative w-full overflow-hidden">
      {/* Atmospheric gradient backdrop */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-purple-50/60 via-white to-white" />

      <div className="relative flex flex-col items-center py-16 sm:py-20">
        {/* Header */}
        <Heading
          preHeadline="Find Your Coach"
          headline="Explore Coaches by City"
          subheadline="Browse our interactive map to discover top coaches in every major city. Click a city to see available coaches and start your journey."
          isH1
          className="mb-12 px-4"
        />

        {/* Map container */}
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-2xl border border-gray-800/30 bg-gray-900 shadow-xl">
            <div className="h-[60vh] sm:aspect-[16/8] sm:h-auto">
              <MapErrorBoundary>
                <Suspense fallback={<MapLoadingSkeleton />}>
                  <LazyCoachMap cities={cities} />
                </Suspense>
              </MapErrorBoundary>
            </div>
          </div>
        </div>

        {/* City directory */}
        <div className="mt-16 w-full max-w-4xl px-4 sm:px-6">
          <CityDirectory
            cityData={cityOptions as RegionWithCountriesOutputGroup[]}
            defaultOpenRegions={["North America", "Europe"]}
          />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_public/coaches/city/")({
  head: () => {
    const breadcrumbLd = createBreadcrumbStructuredData([
      { name: "Home", url: "https://dreamcoachmatch.com" },
      { name: "Coaches", url: "https://dreamcoachmatch.com/coaches" },
      { name: "Cities", url: "https://dreamcoachmatch.com/coaches/city" },
    ]);
    return {
      ...createBrandMeta({
        title: "Coaches by City",
        description: "Interactive map of coach locations by city.",
        canonical: "https://dreamcoachmatch.com/coaches/city",
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
      ],
    };
  },
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["cities"],
        queryFn: () => getCities(),
      }),
      context.queryClient.ensureQueryData({
        queryKey: ["cityOptions"],
        queryFn: () => getCityOptions(),
      }),
    ]);
    return {};
  },
  component: CoachesCityIndexPage,
  staticData: { title: "Coaches by City" },
});
